import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, MessageSquare, Paperclip, X } from 'lucide-react';
import { axiosInstance } from '@/App';

export default function ClientPortalTickets() {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '' });
  const [attachments, setAttachments] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, filter]);

  const loadTickets = async () => {
    try {
      const response = await axiosInstance.get('/client-portal/tickets');
      setTickets(response.data);
    } catch (error) {
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    if (filter === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter(t => t.status.toLowerCase() === filter));
    }
  };

  const handleFileChange = (e, isReply = false) => {
    const files = Array.from(e.target.files);
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    
    if (totalSize > 4 * 1024 * 1024) {
      toast.error('El tamaño total de archivos no puede superar 4MB');
      return;
    }
    
    if (isReply) {
      setReplyAttachments(files);
    } else {
      setAttachments(files);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('subject', newTicket.subject);
      formData.append('description', newTicket.description);
      
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      await axiosInstance.post('/client-portal/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Ticket creado correctamente');
      setCreateDialogOpen(false);
      setNewTicket({ subject: '', description: '' });
      setAttachments([]);
      loadTickets();
    } catch (error) {
      toast.error('Error al crear ticket');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('Escribe una respuesta');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('message', replyText);
      
      replyAttachments.forEach(file => {
        formData.append('attachments', file);
      });

      await axiosInstance.post(`/client-portal/tickets/${selectedTicket.id}/reply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Respuesta enviada');
      setReplyText('');
      setReplyAttachments([]);
      loadTickets();
      
      // Reload ticket details
      const response = await axiosInstance.get(`/client-portal/tickets/${selectedTicket.id}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Error al enviar respuesta');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      open: { label: 'Abierto', class: 'bg-blue-100 text-blue-700' },
      'in progress': { label: 'En Proceso', class: 'bg-yellow-100 text-yellow-700' },
      closed: { label: 'Cerrado', class: 'bg-gray-100 text-gray-700' },
      resolved: { label: 'Resuelto', class: 'bg-green-100 text-green-700' }
    };
    const config = statusMap[status.toLowerCase()] || statusMap.open;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#9C0F17] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/portal-cliente/dashboard')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <h1 className="text-2xl font-bold">Mis Tickets</h1>
            </div>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-[#9C0F17] hover:bg-gray-100">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Ticket
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Nuevo Ticket</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Asunto</Label>
                    <Input
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                      placeholder="Resumen del problema"
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                      placeholder="Describe detalladamente el problema"
                      rows={4}
                    />
                  </div>
                  <div>
                    <Label>Adjuntos (máx 4MB total)</Label>
                    <Input
                      type="file"
                      multiple
                      onChange={(e) => handleFileChange(e, false)}
                    />
                    {attachments.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {attachments.length} archivo(s) seleccionado(s)
                      </p>
                    )}
                  </div>
                  <Button onClick={handleCreateTicket} className="w-full bg-[#9C0F17]">
                    Crear Ticket
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Abiertos</SelectItem>
              <SelectItem value="in progress">En Proceso</SelectItem>
              <SelectItem value="resolved">Resueltos</SelectItem>
              <SelectItem value="closed">Cerrados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Cargando tickets...</p>
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">No hay tickets con este filtro</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTickets.map(ticket => (
              <Card
                key={ticket.id}
                className="hover:shadow-lg transition cursor-pointer"
                onClick={() => {
                  setSelectedTicket(ticket);
                  setDetailDialogOpen(true);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">{ticket.subject}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2">{ticket.description}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Creado: {new Date(ticket.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <div className="ml-4">
                      {getStatusBadge(ticket.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Ticket Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedTicket.subject}</span>
                  {getStatusBadge(selectedTicket.status)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div>
                  <p className="text-gray-700">{selectedTicket.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(selectedTicket.created_at).toLocaleString('es-ES')}
                  </p>
                </div>

                {/* Responses */}
                {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold">Respuestas</h4>
                    {selectedTicket.responses.map((response, idx) => (
                      <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700">{response.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {response.user_name} - {new Date(response.created_at).toLocaleString('es-ES')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Form */}
                {selectedTicket.status.toLowerCase() !== 'closed' && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-semibold">Responder</h4>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Escribe tu respuesta..."
                      rows={3}
                    />
                    <div>
                      <Label>Adjuntar archivos (máx 4MB total)</Label>
                      <Input
                        type="file"
                        multiple
                        onChange={(e) => handleFileChange(e, true)}
                      />
                    </div>
                    <Button onClick={handleReply} className="bg-[#9C0F17]">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Enviar Respuesta
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
